<template>
  <div
    data-event-chip
    draggable="false"
    class="absolute rounded overflow-hidden cursor-pointer group select-none touch-none"
    :class="[
      segment.continuesBefore ? 'rounded-t-none' : '',
      segment.continuesAfter  ? 'rounded-b-none' : '',
    ]"
    :style="cardStyle"
    role="button"
    tabindex="0"
    :aria-label="ariaLabel"
    :title="blockedReason || undefined"
    @click.stop="onActivate"
    @keydown.enter.prevent="onActivate"
    @keydown.space.prevent="onActivate"
    @pointerdown.stop="onPointerDown"
  >
    <!-- Resize handles. Only on the real edges of the event: a segment that
         continues onto another day has no draggable edge here. -->
    <div
      v-if="draggable && !segment.continuesBefore"
      class="absolute inset-x-0 top-0 h-1.5 cursor-ns-resize z-10"
      @pointerdown.stop.prevent="(e) => emit('drag-start', e, 'resize-start', booking, segment)"
    ></div>
    <div
      v-if="draggable && !segment.continuesAfter"
      class="absolute inset-x-0 bottom-0 h-1.5 cursor-ns-resize z-10"
      @pointerdown.stop.prevent="(e) => emit('drag-start', e, 'resize-end', booking, segment)"
    ></div>

    <div class="px-1.5 py-0.5 h-full overflow-hidden" :style="textStyle">
      <p class="text-[12px] leading-tight flex items-center gap-1 min-w-0">
        <svg v-if="booking.internal_notes" class="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-label="Has private notes">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
        </svg>
        <span class="truncate">{{ title }}</span>
      </p>
      <p v-if="tall" class="text-[12px] leading-tight truncate">
        {{ timeLabel }}
      </p>
      <p v-if="taller && mainLocation" class="text-[12px] leading-tight truncate">
        {{ mainLocation }}
      </p>
    </div>
  </div>
</template>

<script setup>
import { computed, onUnmounted } from 'vue'
import { useCalendarSettingsStore } from 'stores/calendarSettings.js'
import {
  useCalendarGeometry, ROW_HEIGHT, MIN_CARD_HEIGHT,
} from 'composables/useCalendarGeometry.js'
import { DRAG_THRESHOLD_PX } from 'composables/useGridDrag.js'

const settingsStore = useCalendarSettingsStore()
const { formatTime, darkenColor, readableTextColor } = useCalendarGeometry()

const props = defineProps({
  // One day's slice of a booking: { booking, startMinutes, endMinutes,
  // continuesBefore, continuesAfter }. Multi-day events produce one segment per
  // day, so the card no longer has to know about days at all.
  segment:  { type: Object, required: true },
  // Side-by-side position from layoutDayEvents, as percentages of the column.
  position: { type: Object, default: () => ({ leftPercent: 0, widthPercent: 100 }) },
  // True while this chip is the one being dragged: it stays in place, dimmed,
  // while the ghost tracks the pointer. It must not be removed from the DOM
  // mid-gesture or the browser cancels the pointer stream.
  dragging: { type: Boolean, default: false },
})

const emit = defineEmits(['click', 'drag-start', 'move-blocked'])

const booking = computed(() => props.segment.booking)
const title   = computed(() => booking.value.title || booking.value.name || '(No title)')

// Google events are draggable too: the move is pushed upstream through the
// Calendar API and the local mirror row follows. Only providers we cannot write
// to are held back, so a chip that looks movable actually is.
const UNWRITABLE_TYPES = ['outlook']

const draggable = computed(() => {
  const b = booking.value
  if (b.context !== 'ExternalBooking') return true
  if (UNWRITABLE_TYPES.includes(b.calendar_type)) return false
  // Only the organizer can change an event's time. Google refuses the write, so
  // a chip somebody else owns must not look draggable in the first place.
  return b.movable !== false
})

// Google Calendar answers a drag it cannot honour with "You can not schedule
// this event" rather than ignoring it. Silence reads as the feature being
// broken, so say why instead.
const blockedReason = computed(() => booking.value.move_blocked_reason || null)

function onPointerDown(event) {
  if (event.button !== 0) return
  if (!draggable.value) {
    if (blockedReason.value) watchForDragAttempt(event)
    return
  }
  emit('drag-start', event, 'move', booking.value, props.segment)
}

// Only answer an actual drag, never a plain click. Clicking a chip opens its
// popover, and explaining that it cannot be moved is noise there. The same
// threshold the drag machine uses, so "far enough to be a drag" means one thing.
let _stopWatching = null

function watchForDragAttempt(event) {
  const startX = event.clientX
  const startY = event.clientY

  const onMove = (moveEvent) => {
    const travelled = Math.hypot(moveEvent.clientX - startX, moveEvent.clientY - startY)
    if (travelled < DRAG_THRESHOLD_PX) return
    emit('move-blocked', blockedReason.value)
    stopWatching()
  }

  const stopWatching = () => {
    window.removeEventListener('pointermove', onMove)
    window.removeEventListener('pointerup', stopWatching)
    window.removeEventListener('pointercancel', stopWatching)
    _stopWatching = null
  }

  _stopWatching?.()
  _stopWatching = stopWatching
  window.addEventListener('pointermove', onMove)
  window.addEventListener('pointerup', stopWatching)
  window.addEventListener('pointercancel', stopWatching)
}

onUnmounted(() => _stopWatching?.())

function onActivate(event) {
  emit('click', booking.value, event.currentTarget.getBoundingClientRect())
}

const top    = computed(() => (props.segment.startMinutes / 60) * ROW_HEIGHT)
const height = computed(() =>
  Math.max(((props.segment.endMinutes - props.segment.startMinutes) / 60) * ROW_HEIGHT, MIN_CARD_HEIGHT)
)

const tall   = computed(() => height.value >= 36)
const taller = computed(() => height.value >= 52) // room for a third line

const mainLocation = computed(() => {
  const loc = booking.value.location
  if (!loc || typeof loc !== 'string') return ''
  return loc.split(',')[0].trim()
})

// color_code = calendar's color (connected cal) or booking's own color (timelydo)
const color = computed(() => booking.value.color_code || '#3b82f6')

const isPast = computed(() => {
  const end = new Date(booking.value.end_at ?? booking.value.end)
  return end < new Date()
})

// The owner's own RSVP is "maybe" — hatch the chip like Google Calendar
const isTentative = computed(() => booking.value.calendar_owner_status === 'tentative')

// Declined: outlined chip, colored struck-through text, no fill
const isDeclined = computed(() => booking.value.calendar_owner_status === 'declined')

const textStyle = computed(() =>
  isDeclined.value
    ? { color: color.value, textDecoration: 'line-through' }
    // White was hardcoded here, which is unreadable on the lighter palette
    // colours (banana, flamingo).
    : { color: readableTextColor(color.value) }
)

const cardStyle = computed(() => {
  const { leftPercent = 0, widthPercent = 100 } = props.position
  const style = {
    top:     `${top.value}px`,
    height:  `${height.value}px`,
    // 1px inset keeps neighbouring chips visually separated without maths on
    // the percentage itself.
    left:    `calc(${leftPercent}% + 1px)`,
    width:   `calc(${widthPercent}% - 3px)`,
    opacity: props.dragging ? '0.35'
           : settingsStore.reduce_brightness_of_past_bookings && isPast.value ? '0.45' : '1',
    cursor: draggable.value ? 'grab' : 'pointer',
  }
  if (isDeclined.value) {
    // Was hardcoded white, which is a bright chip on a dark grid.
    style.backgroundColor = 'transparent'
    style.border = `1.5px solid ${color.value}`
  } else if (isTentative.value) {
    // Thin darker lines of the same color over the solid base
    const dark = darkenColor(color.value, 0.3)
    style.backgroundImage =
      `repeating-linear-gradient(45deg, ${color.value} 0px, ${color.value} 8px, ${dark} 8px, ${dark} 10px)`
  } else {
    style.backgroundColor = color.value
  }
  return style
})

const timeLabel = computed(() =>
  `${formatTime(booking.value.start_at ?? booking.value.start)} to ${formatTime(booking.value.end_at ?? booking.value.end)}`
)

const ariaLabel = computed(() => `${title.value}, ${timeLabel.value}`)
</script>
